import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Plus, Trash2, Building2, Wallet, CreditCard, Receipt, Check, Pencil, X, ArrowRight, User, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { AddClientModal } from '@/components/modals/AddClientModal';
import { AddDepositModal } from '@/components/modals/AddDepositModal';
import { AddCompanyModal } from '@/components/modals/AddCompanyModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CLIENT_COLORS = [
  { bg: 'from-blue-500 to-blue-600', light: 'bg-blue-50', text: 'text-blue-700', accent: '#3B82F6' },
  { bg: 'from-amber-500 to-amber-600', light: 'bg-amber-50', text: 'text-amber-700', accent: '#F59E0B' },
  { bg: 'from-emerald-500 to-emerald-600', light: 'bg-emerald-50', text: 'text-emerald-700', accent: '#10B981' },
  { bg: 'from-violet-500 to-violet-600', light: 'bg-violet-50', text: 'text-violet-700', accent: '#8B5CF6' },
  { bg: 'from-rose-500 to-rose-600', light: 'bg-rose-50', text: 'text-rose-700', accent: '#F43F5E' },
  { bg: 'from-cyan-500 to-cyan-600', light: 'bg-cyan-50', text: 'text-cyan-700', accent: '#06B6D4' },
];

export const Clients = () => {
  const { t, i18n } = useTranslation();
  const { isAdmin, user } = useAuth();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientBalance, setClientBalance] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddClient, setShowAddClient] = useState(false);
  const [showAddDeposit, setShowAddDeposit] = useState(false);
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [editingClientId, setEditingClientId] = useState(null);
  const [editingDeposit, setEditingDeposit] = useState(null);
  const [editDepositData, setEditDepositData] = useState({});
  const [editingClientName, setEditingClientName] = useState('');
  const [clientBalances, setClientBalances] = useState({});

  const isRTL = i18n.language === 'ar';
  const isClient = user?.role === 'client';

  useEffect(() => {
    if (isClient && user?.client_id) {
      loadClientData(user.client_id);
    } else {
      fetchClients();
    }
  }, []);

  const loadClientData = async (clientId) => {
    try {
      const clientRes = await axios.get(`${API}/clients/${clientId}`);
      setSelectedClient(clientRes.data);
      setClients([clientRes.data]);
      await fetchClientDetails(clientId);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
      // Fetch balances for all clients
      const balances = {};
      await Promise.all(response.data.map(async (c) => {
        try {
          const res = await axios.get(`${API}/clients/${c.id}/balance`);
          balances[c.id] = res.data;
        } catch { balances[c.id] = { balance: 0, total_deposits: 0, total_spent: 0 }; }
      }));
      setClientBalances(balances);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const fetchClientDetails = async (clientId) => {
    try {
      const [balanceRes, depositsRes, companiesRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}/balance`),
        axios.get(`${API}/deposits?client_id=${clientId}`),
        axios.get(`${API}/companies?client_id=${clientId}`)
      ]);
      setClientBalance(balanceRes.data);
      setDeposits(depositsRes.data);
      setCompanies(companiesRes.data);
    } catch (error) { console.error('Error:', error); }
  };

  const handleSelectClient = async (client) => {
    setSelectedClient(client);
    await fetchClientDetails(client.id);
  };

  const handleDeleteDeposit = async (depositId) => {
    if (window.confirm(t('areYouSure'))) {
      try {
        await axios.delete(`${API}/deposits/${depositId}`);
        toast.success(t('success'));
        fetchClientDetails(selectedClient.id);
      } catch { toast.error(t('error')); }
    }
  };

  const handleSaveDeposit = async (depositId) => {
    try {
      await axios.put(`${API}/deposits/${depositId}`, editDepositData);
      toast.success(isRTL ? 'تم تعديل الإيداع' : 'Deposit updated');
      setEditingDeposit(null);
      fetchClientDetails(selectedClient.id);
    } catch { toast.error(t('error')); }
  };

  const handleDeleteCompany = async (companyId) => {
    if (window.confirm(t('areYouSure'))) {
      try {
        await axios.delete(`${API}/companies/${companyId}`);
        toast.success(t('success'));
        fetchClientDetails(selectedClient.id);
      } catch { toast.error(t('error')); }
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (window.confirm(t('areYouSure'))) {
      try {
        await axios.delete(`${API}/clients/${clientId}`);
        toast.success(t('success'));
        if (selectedClient?.id === clientId) setSelectedClient(null);
        fetchClients();
      } catch { toast.error(t('error')); }
    }
  };

  const handleUpdateClientName = async (clientId) => {
    if (!editingClientName.trim()) { toast.error(t('error')); return; }
    try {
      const client = clients.find(c => c.id === clientId);
      await axios.put(`${API}/clients/${clientId}`, { name: editingClientName.trim(), phone: client?.phone || '', language: client?.language || 'ar' });
      toast.success(t('success'));
      setEditingClientId(null);
      fetchClients();
      if (selectedClient?.id === clientId) setSelectedClient({ ...selectedClient, name: editingClientName.trim() });
    } catch { toast.error(t('error')); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: 'var(--theme-accent)' }} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={isRTL ? 'text-right' : 'text-left'}>
          <h1 className="text-2xl font-bold text-slate-900">
            {selectedClient ? selectedClient.name : (isClient ? (isRTL ? 'حسابي' : 'My Account') : t('clients'))}
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {selectedClient
              ? `${deposits.length} ${isRTL ? 'إيداع' : 'deposits'} · ${companies.length} ${isRTL ? 'منشأة' : 'companies'}`
              : `${clients.length} ${isRTL ? 'عميل مسجل' : 'registered clients'}`}
          </p>
        </div>
        <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          {selectedClient && !isClient && (
            <Button variant="outline" onClick={() => { setSelectedClient(null); setClientBalance(null); }}
              className={`gap-2 h-9 ${isRTL ? 'flex-row-reverse' : ''}`} data-testid="back-to-clients-btn">
              {isRTL ? <ArrowRight className="w-4 h-4" /> : <ArrowRight className="w-4 h-4 rotate-180" />}
              {isRTL ? 'جميع العملاء' : 'All Clients'}
            </Button>
          )}
          {!selectedClient && isAdmin && (
            <Button onClick={() => setShowAddClient(true)} className="h-9 px-4 gap-2"
              style={{ backgroundColor: 'var(--theme-accent)', color: 'var(--theme-accent-foreground)' }}
              data-testid="add-client-btn">
              <Plus className="w-4 h-4" />
              {t('addClient')}
            </Button>
          )}
        </div>
      </div>

      {/* Add Client Form */}
      {showAddClient && (
        <AddClientModal onClose={() => setShowAddClient(false)} onSuccess={fetchClients} />
      )}

      {/* ============ CLIENTS GRID ============ */}
      {!selectedClient && !isClient && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="clients-grid">
          {clients.map((client, index) => {
            const color = CLIENT_COLORS[index % CLIENT_COLORS.length];
            const bal = clientBalances[client.id] || {};
            const isEditing = editingClientId === client.id;

            return (
              <div key={client.id} data-testid={`client-card-${client.id}`}
                className="group rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-lg hover:border-slate-300 transition-all duration-300">
                {/* Color strip */}
                <div className={`h-2 bg-gradient-to-r ${color.bg}`} />
                <div className="p-5">
                  <div className={`flex items-start gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${color.bg} flex items-center justify-center shrink-0 shadow-sm`}>
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : 'text-left'}`}>
                      {isEditing ? (
                        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                          <Input value={editingClientName} onChange={e => setEditingClientName(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleUpdateClientName(client.id); if (e.key === 'Escape') setEditingClientId(null); }}
                            className="h-8 text-sm" autoFocus data-testid={`edit-client-name-input-${client.id}`} />
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-emerald-500" data-testid={`save-client-name-${client.id}`}
                            onClick={e => { e.stopPropagation(); handleUpdateClientName(client.id); }}>
                            <Check className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400"
                            onClick={e => { e.stopPropagation(); setEditingClientId(null); }}>
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <h3 className="font-bold text-slate-800 text-base truncate">{client.name}</h3>
                          {client.phone && (
                            <div className={`flex items-center gap-1.5 mt-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
                              <Phone className="w-3 h-3 text-slate-400" />
                              <span className="text-xs text-slate-400 font-mono" dir="ltr">{client.phone}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    {/* Admin actions */}
                    {isAdmin && !isEditing && (
                      <div className={`flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:bg-blue-50" data-testid={`edit-client-${client.id}`}
                          onClick={e => { e.stopPropagation(); setEditingClientId(client.id); setEditingClientName(client.name); }}>
                          <Pencil className="w-3 h-3 text-blue-500" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 hover:bg-red-50"
                          onClick={e => { e.stopPropagation(); handleDeleteClient(client.id); }}>
                          <Trash2 className="w-3 h-3 text-red-500" />
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Balance preview */}
                  <div className={`grid grid-cols-3 gap-2 mt-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                    <div className="bg-blue-50 rounded-lg p-2 text-center">
                      <p className="text-[10px] text-blue-500 font-medium">{isRTL ? 'الإيداعات' : 'Deposits'}</p>
                      <p className="text-sm font-bold text-blue-700" dir="ltr">{(bal.total_deposits || 0).toLocaleString()}</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-2 text-center">
                      <p className="text-[10px] text-red-500 font-medium">{isRTL ? 'المصروفات' : 'Spent'}</p>
                      <p className="text-sm font-bold text-red-700" dir="ltr">{(bal.total_spent || 0).toLocaleString()}</p>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-2 text-center">
                      <p className="text-[10px] text-emerald-500 font-medium">{isRTL ? 'الرصيد' : 'Balance'}</p>
                      <p className="text-sm font-bold text-emerald-700" dir="ltr">{(bal.balance || 0).toLocaleString()}</p>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className={`flex items-center justify-between mt-4 pt-3 border-t border-slate-100 cursor-pointer ${isRTL ? 'flex-row-reverse' : ''}`}
                    onClick={() => handleSelectClient(client)}>
                    <span className={`text-xs font-medium ${color.text}`}>
                      {isRTL ? 'عرض التفاصيل' : 'View Details'}
                    </span>
                    <div className={`w-7 h-7 rounded-lg ${color.light} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      {isRTL
                        ? <ArrowRight className="w-3.5 h-3.5 rotate-180" style={{ color: color.accent }} />
                        : <ArrowRight className="w-3.5 h-3.5" style={{ color: color.accent }} />}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ============ CLIENT DETAILS ============ */}
      {selectedClient && (
        <div className="space-y-5">
          {/* Balance Stats */}
          <div className={`grid grid-cols-3 gap-4 ${isRTL ? '[direction:rtl]' : ''}`}>
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 border-0 rounded-xl shadow-lg">
              <CardContent className="p-5" dir="ltr">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-xs mb-1">{t('totalDeposits')}</p>
                    <p className="text-2xl font-bold text-white">{clientBalance?.total_deposits?.toFixed(0) || 0}</p>
                    <p className="text-blue-100 text-xs">AED</p>
                  </div>
                  <Wallet className="w-8 h-8 text-white/50" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-red-500 to-red-600 border-0 rounded-xl shadow-lg">
              <CardContent className="p-5" dir="ltr">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-red-100 text-xs mb-1">{t('totalSpent')}</p>
                    <p className="text-2xl font-bold text-white">{clientBalance?.total_spent?.toFixed(0) || 0}</p>
                    <p className="text-red-100 text-xs">AED</p>
                  </div>
                  <Receipt className="w-8 h-8 text-white/50" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 border-0 rounded-xl shadow-lg">
              <CardContent className="p-5" dir="ltr">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-emerald-100 text-xs mb-1">{t('balance')}</p>
                    <p className="text-2xl font-bold text-white">{clientBalance?.balance?.toFixed(0) || 0}</p>
                    <p className="text-emerald-100 text-xs">AED</p>
                  </div>
                  <CreditCard className="w-8 h-8 text-white/50" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Deposits Section */}
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className={`flex items-center justify-between px-5 py-4 border-b border-slate-100 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`flex items-center gap-2.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Wallet className="w-4 h-4 text-amber-600" />
                </div>
                <span className="font-semibold text-slate-800">{t('deposits')}</span>
                <span className="text-xs text-slate-400">({deposits.length})</span>
              </div>
              {isAdmin && (
                <Button size="sm" onClick={() => setShowAddDeposit(!showAddDeposit)} className="h-8 px-3 text-xs gap-1.5"
                  style={{ backgroundColor: 'var(--theme-primary, #0F172A)' }}>
                  <Plus className="w-3.5 h-3.5" /> {t('addDeposit')}
                </Button>
              )}
            </div>
            <div className="p-4 space-y-3">
              {showAddDeposit && (
                <AddDepositModal clientId={selectedClient.id}
                  onClose={() => setShowAddDeposit(false)}
                  onSuccess={() => fetchClientDetails(selectedClient.id)} />
              )}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      <TableHead className="font-semibold text-slate-600 text-xs uppercase">{t('amount')}</TableHead>
                      <TableHead className="font-semibold text-slate-600 text-xs uppercase">{t('depositDate')}</TableHead>
                      <TableHead className="font-semibold text-slate-600 text-xs uppercase">{isRTL ? 'خطط مرتبطة' : 'Linked Plans'}</TableHead>
                      <TableHead className="font-semibold text-slate-600 text-xs uppercase">{t('notes')}</TableHead>
                      {isAdmin && <TableHead className="font-semibold text-slate-600 text-xs uppercase">{t('actions')}</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deposits.map((deposit) => {
                      const linkedCount = deposit.linked_plan_ids?.length || 0;
                      const isEditing = editingDeposit === deposit.id;
                      return (
                        <TableRow key={deposit.id} className="hover:bg-slate-50/80">
                          <TableCell className="font-semibold text-emerald-600" dir="ltr">
                            {isEditing ? (
                              <input type="number" value={editDepositData.amount ?? ''} onChange={e => setEditDepositData(d => ({ ...d, amount: e.target.value }))}
                                className="w-24 border border-slate-300 rounded px-2 py-1 text-sm" dir="ltr" />
                            ) : `${deposit.amount} AED`}
                          </TableCell>
                          <TableCell className="text-slate-600" dir="ltr">
                            {isEditing ? (
                              <input type="date" value={editDepositData.deposit_date ?? ''} onChange={e => setEditDepositData(d => ({ ...d, deposit_date: e.target.value }))}
                                className="border border-slate-300 rounded px-2 py-1 text-sm" />
                            ) : new Date(deposit.deposit_date).toLocaleDateString('en-GB')}
                          </TableCell>
                          <TableCell>
                            {linkedCount > 0 ? (
                              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">
                                {linkedCount} {isRTL ? 'خطة' : 'plans'}
                              </span>
                            ) : <span className="text-xs text-slate-300">-</span>}
                          </TableCell>
                          <TableCell className="text-slate-500">
                            {isEditing ? (
                              <input type="text" value={editDepositData.notes ?? ''} onChange={e => setEditDepositData(d => ({ ...d, notes: e.target.value }))}
                                className="w-full border border-slate-300 rounded px-2 py-1 text-sm" />
                            ) : (deposit.notes || '-')}
                          </TableCell>
                          {isAdmin && (
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {isEditing ? (
                                  <>
                                    <Button size="sm" variant="ghost" className="text-emerald-500 hover:bg-emerald-50" onClick={() => handleSaveDeposit(deposit.id)} data-testid={`save-deposit-${deposit.id}`}>
                                      <Check className="w-4 h-4" />
                                    </Button>
                                    <Button size="sm" variant="ghost" className="text-slate-500 hover:bg-slate-50" onClick={() => setEditingDeposit(null)} data-testid={`cancel-edit-deposit-${deposit.id}`}>
                                      <X className="w-4 h-4" />
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Button size="sm" variant="ghost" className="text-blue-500 hover:bg-blue-50" onClick={() => {
                                      setEditingDeposit(deposit.id);
                                      setEditDepositData({ amount: deposit.amount, deposit_date: deposit.deposit_date?.split('T')[0], notes: deposit.notes || '' });
                                    }} data-testid={`edit-deposit-${deposit.id}`}>
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button size="sm" variant="ghost" className="text-red-500 hover:bg-red-50" onClick={() => handleDeleteDeposit(deposit.id)} data-testid={`delete-deposit-${deposit.id}`}>
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                    {deposits.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={isAdmin ? 5 : 4} className="text-center py-8 text-slate-400">{t('noData')}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>

          {/* Companies Section */}
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className={`flex items-center justify-between px-5 py-4 border-b border-slate-100 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`flex items-center gap-2.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-blue-600" />
                </div>
                <span className="font-semibold text-slate-800">{t('companies')}</span>
                <span className="text-xs text-slate-400">({companies.length})</span>
              </div>
              {isAdmin && (
                <Button size="sm" onClick={() => setShowAddCompany(!showAddCompany)} className="h-8 px-3 text-xs gap-1.5"
                  style={{ backgroundColor: 'var(--theme-primary, #0F172A)' }}>
                  <Plus className="w-3.5 h-3.5" /> {t('addCompany')}
                </Button>
              )}
            </div>
            <div className="p-4 space-y-3">
              {showAddCompany && (
                <AddCompanyModal clientId={selectedClient.id}
                  onClose={() => setShowAddCompany(false)}
                  onSuccess={() => fetchClientDetails(selectedClient.id)} />
              )}
              <div className={`grid grid-cols-2 md:grid-cols-3 gap-3`}>
                {companies.map((company) => (
                  <div key={company.id} className={`p-3.5 bg-slate-50 rounded-xl border border-slate-200 flex justify-between items-center hover:bg-slate-100 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className={`flex items-center gap-2.5 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                        <Building2 className="w-4 h-4 text-blue-500" />
                      </div>
                      <span className="text-sm font-medium text-slate-700">{company.name}</span>
                    </div>
                    {isAdmin && (
                      <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600 hover:bg-red-50 h-7 w-7 p-0" onClick={() => handleDeleteCompany(company.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                ))}
                {companies.length === 0 && (
                  <div className="col-span-full text-center py-8 text-slate-400">
                    <Building2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">{t('noData')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
